import { CopyIcon, TrashIcon } from 'assets/icons';
import CardIconButton from 'components/card/CardIconButton';
import { CalendarDays, ClipboardList } from 'lucide-react';
import TaskAssigneeField from './TaskAssigneeField';
import EmployeeAvatar from 'components/assign/EmployeeAvatar';
import Button from 'components/button';
import RichSelect from 'components/crm/ui/RichSelect';
import TextField from 'components/crm/ui/TextField';
import AppointmentForm from 'components/planning/AppointmentForm';
import SlideOverBody from 'components/SlideOver/component/body';
import { SkeletonLine } from 'components/skeleton/Skeleton';
import SlideOverFooter from 'components/SlideOver/component/footer';
import SlideOverHeader from 'components/SlideOver/component/header';
import useSlideOver from 'components/SlideOver/useSlideOver';
import StyledDateInput from 'components/styledInput/StyledDateInput';
import StyledInput from 'components/styledInput/StyledInput';
import { companyRoleLabel } from 'constants/companyRoles';
import { taskTypeLabel } from 'constants/taskTypes';
import { fr } from 'date-fns/locale';
import { apiErrorText } from 'helper/apiErrorText';
import { contactLinkIdOf, linkKey, linkOptionsOf, linkPayload } from 'helper/taskLinks';
import { addDays, dayLabel, fromIsoDay, isoDay, parseClock } from 'helper/timeLabels';
import UseContext from 'hooks/useContext';
import useCrmContext from 'hooks/useCrmContext';
import useTaskPanels from 'hooks/useTaskPanels';
import { useNotifyTasksChanged } from 'hooks/useTasksChanged';
import useText from 'hooks/useText';
import { useEffect, useRef, useState } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { toast } from 'react-toastify';
import crmApi from 'server/crm/crmApi';
import TaskLinksField, { LINKS_PLACEHOLDER } from './TaskLinksField';

registerLocale('fr-FR', fr);

// A task has a single kind: a call goes in the call list. An old Appel,
// E-mail, Visite or Réunion task becomes a plain task when saved. A new one
// may be a rendez-vous instead: the form turns into the planning's.
const TASK_TYPE = 'todo';
const MEETING = 'meeting';
// Tâche or Rendez-vous: a call is a rendez-vous « Téléphonique ».
const kindOf = (type) => (type === MEETING ? MEETING : TASK_TYPE);
// « Échéance »: a day counted from today, or one picked in the calendar.
const DUE_CHOICES = [
  ['today', 0, 'Aujourd’hui'],
  ['tomorrow', 1, 'Demain'],
  ['in3', 3, 'Dans 3 jours'],
  ['in7', 7, 'Dans 1 semaine'],
];
// A field of the form to come: its label and its hint (known already), a
// grey box between them, each as the field draws it.
const GhostField = ({ label, hint, hintClass = 'text-xs font-normal text-faint', boxClass = 'h-10' }) => (
  <div className="flex flex-col gap-1.5">
    <span className="text-xs font-normal leading-4 text-gray-500">{label}</span>
    <span aria-hidden="true" className={`block animate-pulse rounded-[0.4rem] bg-gray-100 ${boxClass}`} />
    {hint && <p className={hintClass}>{hint}</p>}
  </div>
);

const dueChoiceOf = (dueOn, now) => DUE_CHOICES.find(([, days]) => isoDay(addDays(now, days)) === dueOn)?.[0] ?? 'pick';
const DEFAULT_DURATION = 15;

const pad = (value) => String(value).padStart(2, '0');

// A link is known by its kind and id: two companies may share a name, so a
// label does not tell them apart.
const linkOf = (options, type, id) => options.find((option) => option.type === type && option.id === id) ?? null;
// A contact known by its name only (an old task without links, or a record
// page that gives the name), among the options (of its company when it has
// one). Two contacts may share a name: a task with links keeps its own ids.
const contactLink = (options, name, companyId, companyName = '') =>
  options.find((option) => option.type === 'contact' && option.label === name && (!companyId || option.companyId === companyId)) ?? {
    type: 'contact',
    id: `name:${name}`,
    label: name,
    sub: companyName,
    companyId: companyId ?? null,
  };

// A contact page gives the person's account too: by it, of two namesakes at
// the same company, the form links the one whose page it is.
const contactLinkOfRef = (options, contactRef, companyId) =>
  contactRef?.userId
    ? options.find(
        (option) => option.type === 'contact' && option.id === contactRef.userId && (!companyId || option.companyId === companyId),
      ) ?? null
    : null;

// A record page opens the form on its company, its group or one of its contacts.
const defaultLinks = (defaults, options) => {
  if (defaults.contactName && defaults.groupId) {
    // A contact of a group has no account: it is not among the options.
    const group = linkOf(options, 'group', defaults.groupId);
    return [...(group ? [group] : []), { type: 'contact', id: `name:${defaults.contactName}`, label: defaults.contactName, companyId: null }];
  }
  if (defaults.contactName) {
    const companyName = linkOf(options, 'company', defaults.companyId)?.label ?? '';
    // A contact without an account (`name:<name>`) is found by their name only.
    return [
      contactLinkOfRef(options, defaults.contactRef, defaults.companyId) ??
        contactLink(options, defaults.contactName, defaults.companyId, companyName),
    ];
  }
  const record = defaults.companyId
    ? linkOf(options, 'company', defaults.companyId)
    : defaults.groupId
      ? linkOf(options, 'group', defaults.groupId)
      : null;
  return record ? [record] : [];
};

// A task without `links` (a real one) is linked to its company, its group
// and its contact; a record missing from the options keeps the task's own
// name, so that it is never dropped silently.
const linksOfTask = (task, options) => {
  if (task.links?.length) return task.links;
  return [
    task.companyId && (linkOf(options, 'company', task.companyId) ?? { type: 'company', id: task.companyId, label: task.company?.name ?? '' }),
    task.groupId && (linkOf(options, 'group', task.groupId) ?? { type: 'group', id: task.groupId, label: task.group?.name ?? '', deleted: task.group?.deleted }),
    task.contactName && contactLink(options, task.contactName, task.companyId, task.company?.name),
  ].filter(Boolean);
};

const emptyForm = (defaults, options) => ({
  title: '',
  note: '',
  dueOn: defaults.dueOn ?? isoDay(new Date()),
  time: defaults.time ?? '',
  duration: defaults.duration ?? DEFAULT_DURATION,
  durationTouched: Boolean(defaults.duration),
  assigneeId: '',
  links: defaultLinks(defaults, options),
});
const formOf = (task, options) => ({
  title: task.title,
  note: task.note ?? '',
  dueOn: task.dueOn,
  time: task.dueTime ? task.dueTime.slice(0, 5) : '',
  duration: task.durationMin ?? DEFAULT_DURATION,
  durationTouched: true,
  assigneeId: task.assigneeId,
  links: linksOfTask(task, options),
});

/**
 * « Nouvelle tâche » and « Modifier la tâche » (slide-over `task_form`, with
 * `{ taskId, defaults }` in `objID`): the title, written by the user (no AI),
 * its description, the day and the time with their shortcuts, the length, the
 * colleague, the records it is linked to (contacts, companies, groups, temp
 * workers). ⌘ or Ctrl + Entrée saves. Why the save button is grey is written
 * beside it. A new task goes to `defaults.assigneeId` if given, else to me.
 * « Type » (a new one only) turns it into a rendez-vous (`defaults.type`
 * 'meeting' opens it so): the planning's AppointmentForm, with what was typed,
 * linked to the first company and the first group chosen, the week beside it.
 */
const TaskFormPanel = () => {
  const { objID } = UseContext();
  const { taskId = null, defaults = {} } = objID && typeof objID === 'object' ? objID : {};
  const { language, text } = useText('taskForm');
  const { text: tasksText } = useText('tasks');
  const { text: fieldsText } = useText('appointmentFields');
  const { call, me } = useCrmContext();
  const { toggleSlideOver } = useSlideOver();
  const { openTask } = useTaskPanels();
  const notify = useNotifyTasksChanged();
  const [task, setTask] = useState(null);
  const [team, setTeam] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [linkOptions, setLinkOptions] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  // « Choisir un jour » keeps the calendar open, even on a day of the menu.
  const [picking, setPicking] = useState(false);
  // The team and the records read: until then their lists are empty.
  const [ready, setReady] = useState(false);
  const [kind, setKind] = useState(kindOf(defaults.type));
  const body = useRef(null);
  const submitRef = useRef(null);
  const editing = Boolean(taskId);
  const fail = (error) => {
    const message = apiErrorText(error, { language, text: tasksText, group: 'errors' });
    if (message) toast.error(message);
  };

  useEffect(() => {
    if (!me) return;
    const { taskId: id = null, defaults: given = {} } = objID && typeof objID === 'object' ? objID : {};
    // A new task needs nothing to be typed in: its fields show at once, the
    // team and the records fill their lists when they come. A task to edit
    // waits for itself.
    setForm(id ? null : { ...emptyForm(given, []), links: [], assigneeId: given.assigneeId || me.id });
    setKind(kindOf(given.type));
    setPicking(false);
    setReady(false);
    Promise.all([
      id ? call(crmApi.getTask, { id }) : Promise.resolve(null),
      call(crmApi.getTeam),
      call(crmApi.getContacts),
    ]).then(
      ([found, people, records]) => {
        const options = linkOptionsOf(records);
        setTask(found);
        setTeam(people);
        setContacts(records);
        setLinkOptions(options);
        setReady(true);
        if (found) setForm(formOf(found, options));
        // The records the opener gave, once known: unless some were chosen meanwhile.
        else setForm((current) => ({ ...current, links: current.links.length ? current.links : defaultLinks(given, options) }));
      },
      fail,
    );
  }, [call, me, objID]);

  // A new task opens on its title, as « N » then typing expects.
  const shown = Boolean(form);
  useEffect(() => {
    if (shown && !editing) body.current?.querySelector('input')?.focus({ preventScroll: true });
  }, [shown, editing]);

  useEffect(() => {
    const onKey = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        event.preventDefault();
        submitRef.current?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const close = () => toggleSlideOver();
  // « Annuler » goes back to the task it edits.
  const cancel = () => (editing ? openTask(taskId) : close());

  if (!form) {
    return (
      <>
        <SlideOverHeader
          hide={close}
          closeLabel={text('close')}
          description={editing ? <SkeletonLine className="mt-1 h-3 w-48" /> : text('newSubtitle')}
        >
          {text(editing ? 'editTitle' : 'newTitle')}
        </SlideOverHeader>
        {/* The form to come, in its shapes: nothing moves once it lands. */}
        <SlideOverBody>
          <div aria-busy="true" className="flex flex-col gap-5">
            {!editing && <GhostField label="Type" hint={text('typeHint_todo')} hintClass="-mt-1 text-xs text-faint" boxClass="h-[42px]" />}
            <GhostField label="Titre" />
            <GhostField label="Associer à" />
            <hr className="border-gray-100" />
            <GhostField label="Assigné à" />
            <div className="grid grid-cols-[7fr_3fr] gap-x-3">
              <GhostField label="Échéance" />
              <GhostField label={text('timeLabel')} />
            </div>
            <GhostField label="Note (facultatif)" boxClass="h-24" />
          </div>
        </SlideOverBody>
        <div className="flex-shrink-0 border-t border-gray-100">
          <SlideOverFooter>
            <span className="mr-auto" />
            <Button color="SECONDARY" size="SM" onClick={cancel}>
              {text('cancel')}
            </Button>
            {/* Its place only: nothing to send yet. */}
            <span aria-hidden="true">
              <Button color="DISABLED" size="SM" disabled>
                {text(editing ? 'save' : 'ctaTodo')}
              </Button>
            </span>
          </SlideOverFooter>
        </div>
      </>
    );
  }

  const set = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const clock = form.time.trim() ? parseClock(form.time) : null;
  const timeOk = !form.time.trim() || Boolean(clock);
  let blocker = null;
  if (!form.title.trim() && !form.note.trim()) blocker = text('blockDescription');
  else if (!timeOk) blocker = text('blockTime');
  const now = new Date();
  const dueChoice = picking ? 'pick' : dueChoiceOf(form.dueOn, now);

  const firstLink = (type) => form.links.find((link) => link.type === type) ?? null;
  const company = firstLink('company');
  const contact = firstLink('contact');
  const contactOption = contact
    ? contacts.find(
        (option) =>
          option.contact && contactLinkIdOf(option) === contact.id && (option.companyId ?? null) === (contact.companyId ?? null),
      )
    : null;
  // The task's own contact, not among the options, keeps its role and phone.
  const taskContact = task ? linksOfTask(task, linkOptions).find((link) => link.type === 'contact') : null;
  const keptContact = !contactOption && contact && taskContact && linkKey(contact) === linkKey(taskContact) ? task : null;
  const fields = () => ({
    type: TASK_TYPE,
    title: form.title.trim(),
    note: form.note.trim(),
    dueOn: form.dueOn,
    dueTime: clock ? `${pad(clock[0])}:${pad(clock[1])}` : null,
    durationMin: Number(form.duration),
    assigneeId: form.assigneeId,
    // The api keeps the links, and deduces companyId, groupId and contactName
    // from them: it ignores these old fields when `links` is present. They are
    // still sent, for the demo and for the detail of a task.
    links: form.links.map(linkPayload),
    companyId: company?.id ?? contact?.companyId ?? null,
    groupId: firstLink('group')?.id ?? null,
    contactName: contact?.label ?? '',
    contactRole: contactOption
      ? typeof contactOption.companyRole === 'number'
        ? companyRoleLabel(language, contactOption.companyRole)
        : (contactOption.role ?? '')
      : (keptContact?.contactRole ?? ''),
    contactDetail: contactOption ? (contactOption.phone ?? '') : (keptContact?.contactDetail ?? ''),
  });

  // Out of today, the toast says when; given to a colleague, to whom.
  const savedMessage = () => {
    const when =
      form.dueOn === isoDay(now)
        ? ''
        : form.dueOn === isoDay(addDays(now, 1))
          ? text('whenTomorrow')
          : text('whenDay', { day: dayLabel(fromIsoDay(form.dueOn)).toLowerCase() });
    const colleague = form.assigneeId !== me.id && form.assigneeId !== (task?.assigneeId ?? me.id);
    if (colleague) {
      const person = team.find((member) => member.id === form.assigneeId);
      return `${text('handed', { name: `${person.firstname} ${person.lastname}`.trim() })}${when}`;
    }
    return editing ? text('saved') : `${text('addedTodo')}${when}`;
  };

  const submit = async () => {
    if (blocker || busy) return;
    setBusy(true);
    try {
      const saved = editing
        ? await call(crmApi.updateTask, { id: taskId, fields: fields() })
        : await call(crmApi.createTask, { fields: fields() });
      // Added from the « Terminées » column of the kanban: done at once.
      if (!editing && defaults.done) await call(crmApi.setTaskDone, { id: saved.id, done: true });
      toast.success(savedMessage());
      notify();
      close();
    } catch (error) {
      fail(error);
    } finally {
      setBusy(false);
    }
  };
  submitRef.current = submit;

  const remove = async () => {
    try {
      await call(crmApi.deleteTask, { id: taskId });
      toast.success(text('deleted'));
      notify();
      close();
    } catch (error) {
      fail(error);
    }
  };
  const duplicate = async () => {
    try {
      await call(crmApi.createTask, { fields: { ...fields(), title: text('copyOf', { title: form.title }) } });
      toast.success(text('duplicated'));
      notify();
      close();
    } catch (error) {
      fail(error);
    }
  };

  // Tâche or Rendez-vous, for a new one only.
  const typeField = !editing && (
    <RichSelect
      id="task-type"
      label={text('typeLabel')}
      value={kind}
      options={[
        { value: TASK_TYPE, label: taskTypeLabel(language, TASK_TYPE), icon: ClipboardList, description: text('typeHelp_todo') },
        { value: MEETING, label: text('typeMeeting'), icon: CalendarDays, iconClass: 'text-primary', description: text('typeHelp_meeting') },
      ]}
      hint={text(`typeHint_${kind}`)}
      onChange={setKind}
    />
  );

  if (kind === MEETING && !editing) {
    // What was typed comes along: the title, the description, the day and
    // the time, the first company (or the contact's) and the contact.
    const start = clock ? `${pad(clock[0])}:${pad(clock[1])}` : '10:00';
    const [hours, minutes] = start.split(':').map(Number);
    const endMinutes = Math.min(hours * 60 + minutes + 60, 23 * 60 + 59);
    const companyId = company?.id ?? contact?.companyId ?? null;
    const companyName = company?.label ?? contact?.sub ?? '';
    const groupId = firstLink('group')?.id ?? null;
    const companyContacts = companyId
      ? contacts.filter((option) => option.companyId === companyId && option.contact).map((option) => ({ name: option.contact }))
      : [];
    // The form reads its record once, when drawn: drawn again once the records
    // are read, so that a rendez-vous opened from a page keeps its company.
    return (
      <div aria-busy={!ready || undefined} className="contents">
        <AppointmentForm
          key={ready ? 'ready' : 'waiting'}
          mode="create"
          layout="panel"
          chooseRecord
          prepend={typeField}
          record={companyId ? { companyId, name: companyName, contacts: companyContacts, contactName: contact?.label ?? '' } : null}
          groupId={groupId}
          initial={{
            title: form.title.trim(),
            description: form.note.trim(),
            startDay: form.dueOn,
            endDay: form.dueOn,
            start,
            end: `${pad(Math.floor(endMinutes / 60))}:${pad(endMinutes % 60)}`,
            ...(form.assigneeId && form.assigneeId !== me.id ? { ownerId: form.assigneeId } : {}),
          }}
          header={
            <SlideOverHeader hide={close} closeLabel={text('close')} description="Planifiez un rendez-vous dans votre agenda.">
              {fieldsText('createTitle')}
            </SlideOverHeader>
          }
          onSaved={() => {
            notify();
            close();
          }}
          onCancel={close}
        />
      </div>
    );
  }

  return (
    <>
      <SlideOverHeader
        hide={close}
        closeLabel={text('close')}
        description={
          editing
            ? text('createdBy', {
                name: task.createdByPerson ? `${task.createdByPerson.firstname} ${task.createdByPerson.lastname}`.trim() : '',
                day: dayLabel(new Date(task.createdAt)).toLowerCase(),
              })
            : defaults.done
              ? // From the « Terminées » column: saved, then ticked at once.
                'Elle sera ajoutée directement à vos tâches terminées.'
              : text('newSubtitle')
        }
      >
        {text(editing ? 'editTitle' : 'newTitle')}
      </SlideOverHeader>
      <SlideOverBody>
        <div ref={body} aria-busy={!ready || undefined} className="flex flex-col gap-5">
          {/* The type first: switching to « Rendez-vous » leaves it in place. */}
          {typeField}
          <StyledInput
            type="text"
            label="Titre"
            value={form.title}
            maxLength={120}
            onChange={(event) => set('title', event.target.value)}
          />
          <TaskLinksField
            label="Associer à"
            openUpwards={false}
            placeholder={LINKS_PLACEHOLDER}
            options={linkOptions}
            value={form.links}
            onChange={(links) => set('links', links)}
          />
          {/* Who and when, apart from what. */}
          <hr className="border-gray-100" />
          <TaskAssigneeField
            label="Assigné à"
            // Oneself is known before the team comes.
            team={team.length ? team : [me]}
            meId={me.id}
            value={form.assigneeId}
            onChange={(assigneeId) => set('assigneeId', assigneeId)}
          />
          {/* The day takes 70 %, its time the rest. */}
          <div className="grid grid-cols-[7fr_3fr] gap-x-3 gap-y-2">
            <RichSelect
              id="task-due"
              compact
              label="Échéance"
              value={dueChoice}
              options={[
                ...DUE_CHOICES.map(([value, , label]) => ({ value, label, icon: CalendarDays, iconClass: 'text-gray-500' })),
                {
                  value: 'pick',
                  // A day picked earlier reads as itself.
                  label: dueChoice === 'pick' && !picking ? dayLabel(fromIsoDay(form.dueOn)) : 'Choisir un jour…',
                  icon: CalendarDays,
                  iconClass: 'text-gray-500',
                },
              ]}
              onChange={(value) => {
                const choice = DUE_CHOICES.find(([key]) => key === value);
                setPicking(!choice);
                if (choice) set('dueOn', isoDay(addDays(now, choice[1])));
              }}
            />
            <TextField
              id="task-form-time"
              type="time"
              label={text('timeLabel')}
              value={form.time}
              onChange={(event) => set('time', event.target.value)}
            />
            {dueChoice === 'pick' && (
              <DatePicker
                dateFormat="dd/MM/yyyy"
                selected={fromIsoDay(form.dueOn)}
                onChange={(date) => date && set('dueOn', isoDay(date))}
                locale="fr-FR"
                popperPlacement="bottom"
                customInput={<StyledDateInput label="Jour" />}
              />
            )}
          </div>
          <StyledInput
            type="textarea"
            label="Note (facultatif)"
            value={form.note}
            maxLength={2000}
            onChange={(event) => set('note', event.target.value)}
          />
        </div>
      </SlideOverBody>
      <div className="flex-shrink-0 border-t border-gray-100">
        <SlideOverFooter>
          {editing && (
            <div className="flex items-center gap-2">
              <CardIconButton label={text('delete')} icon={<TrashIcon wh={18} color="#DC2626" />} onClick={remove} />
              <CardIconButton label={text('duplicate')} icon={<CopyIcon wh={18} />} onClick={duplicate} />
            </div>
          )}
          <span className="mr-auto text-xs text-gray-500">{blocker}</span>
          <Button color="SECONDARY" size="SM" onClick={cancel}>
            {text('cancel')}
          </Button>
          <Button color={blocker || busy ? 'DISABLED' : 'PRIMARY'} size="SM" disabled={Boolean(blocker) || busy} onClick={submit}>
            {busy
              ? text('busy')
              : editing
                ? text('save')
                : defaults.done
                  ? 'Ajouter comme terminée'
                  : text('ctaTodo')}
          </Button>
        </SlideOverFooter>
      </div>
    </>
  );
};

export default TaskFormPanel;
